import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Acknowledge, Button, Container, LinearProgress, Stack, Text } from '../components'

/**
 * FLIGHT-850: where the "stop the remaining emails in this series" footer link
 * lands.
 *
 * Unauthenticated by design - someone stopping unwanted email should not have
 * to log in first. The token in the URL is the capability; the API will only
 * ever let it stop the one series it belongs to.
 *
 * Two steps on purpose. The GET only reads the series name, so a mail client
 * or scanner prefetching the link cannot opt anyone out; the recipient's own
 * click is what sends the POST.
 *
 * Worded as leaving one series, never as a GiveWise unsubscribe, because that
 * is exactly what it is: their other GiveWise email and their HubSpot
 * subscription preferences are untouched.
 */
export function EmailFlowOptOut() {
  const { token } = useParams()
  const [state, setState] = useState({ status: 'loading' })

  // Same env var the Apollo client builds its URI from (graphql/apollo-client.js).
  const endpoint = `${process.env.REACT_APP_API_HOST}/api/email-flow-opt-out/${token}`

  useEffect(() => {
    let cancelled = false
    fetch(endpoint)
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('invalid'))))
      .then((data) => {
        if (cancelled) return
        setState({ status: data.alreadyOptedOut ? 'done' : 'confirm', flowName: data.flowName })
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'invalid' })
      })
    return () => {
      cancelled = true
    }
  }, [endpoint])

  const confirm = useCallback(() => {
    setState((prev) => ({ ...prev, status: 'submitting' }))
    fetch(endpoint, { method: 'POST' })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('failed'))))
      .then((data) => setState({ status: 'done', flowName: data.flowName }))
      .catch(() => setState({ status: 'error' }))
  }, [endpoint])

  return (
    <Container maxWidth="lg">
      <Stack justifyItems="center">
        <Stack>
          <Text.H1>Email preferences</Text.H1>
        </Stack>

        {(state.status === 'loading' || state.status === 'submitting') && <LinearProgress />}

        {/* Built from Text and Button rather than an Acknowledge.* variant:
            Success, Warning and Error all carry a status icon and tone that
            would be wrong for a neutral "are you sure?" prompt, and there is
            no neutral variant. Flagged as a gap on FLIGHT-660 (FLIGHT-777)
            rather than filled with a new component here. */}
        {state.status === 'confirm' && (
          <Stack spacing={2}>
            <Text.H5>Stop receiving &quot;{state.flowName}&quot;?</Text.H5>
            <Text.Body>
              You will not receive any more emails in this series. This does not change any of your other GiveWise email
              preferences.
            </Text.Body>
            <Button onClick={confirm}>Yes, stop these emails</Button>
          </Stack>
        )}

        {state.status === 'done' && (
          <Acknowledge.Success
            title="You have been removed from this series"
            content={`You will not receive any more emails from "${state.flowName}". Your other GiveWise email preferences are unchanged.`}
          />
        )}

        {state.status === 'invalid' && (
          <Acknowledge.Error
            title="This link is no longer valid"
            content="We could not find an email series for this link. If you are still receiving emails you do not want, please contact us at support@givewise.ca."
          />
        )}

        {state.status === 'error' && (
          <Acknowledge.Error
            title="Sorry, that did not work"
            content="We could not update your preference just now. Please try the link again, or contact us at support@givewise.ca."
          />
        )}
      </Stack>
    </Container>
  )
}
